import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createId } from '../../utils/createId';
import { upperCaseFirst } from '../../utils/upperCaseFirst';
import { Context, KeyboardEventOptions, KeyboardNavigationConfig } from './context';

const parseCode = (event: KeyboardEvent): string => {
  let code = '';
  if (event.shiftKey && !event.code?.startsWith('Shift')) {
    code += 'Shift';
  }

  if (event.code?.startsWith('Key')) {
    code += upperCaseFirst(event.code.substring(3));
  } else {
    code += upperCaseFirst(event.code || event.key);
  }

  return code;
};

const DEFAULT_OPTIONS: KeyboardEventOptions = {
  isEnabled: true,
  preventDefault: true,
};

interface QueueEntry {
  options: KeyboardEventOptions;
  config: KeyboardNavigationConfig;
  id: string;
}

export const Provider: React.FC<React.PropsWithChildren<React.PropsWithChildren>> = ({
  children,
}) => {
  const queue = useRef<QueueEntry[]>([]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const currentQueue = queue.current;
      let i = currentQueue.length - 1;
      const code = parseCode(event);
      while (i >= 0) {
        const highestPriorityEntry = currentQueue[i];
        const handler = highestPriorityEntry.config[code];
        if (handler && highestPriorityEntry.options.isEnabled) {
          if (highestPriorityEntry.options.preventDefault) {
            event.preventDefault();
          }
          handler(event);
          return;
        }
        i--;
      }
    };

    document.body.addEventListener('keydown', handleKeydown);

    return () => document.body.removeEventListener('keydown', handleKeydown);
  }, []);

  const subscribe = useCallback(
    (config: KeyboardNavigationConfig, options: Partial<KeyboardEventOptions> = {}) => {
      const id = createId(5);

      queue.current.push({
        id,
        config,
        options: { ...DEFAULT_OPTIONS, ...options },
      });

      return id;
    },
    []
  );

  const updateConfig = useCallback(
    (id: string, config: KeyboardNavigationConfig, options: Partial<KeyboardEventOptions> = {}) => {
      const currentIndex = queue.current.findIndex((entry) => entry.id === id);

      if (currentIndex < 0) {
        throw new Error(`No entry with id ${id} found`);
      }

      queue.current[currentIndex] = {
        id,
        config,
        options: { ...DEFAULT_OPTIONS, ...options },
      };
    },
    []
  );

  const unsubscribe = useCallback((id: string) => {
    queue.current = queue.current.filter((entry) => entry.id !== id);
  }, []);

  const contextValue = useMemo(
    () => ({
      subscribe,
      updateConfig,
      unsubscribe,
    }),
    [subscribe, updateConfig, unsubscribe]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};
